'use client';

import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { toastBase } from '@/fo/ui/kit/toast/base';
import { utilFormatPhoneNumber } from '@/fo/util/format-phone-number';

import { InputOTP } from '../components/InputOTP/InputOTP';
import { InputOTPGroup } from '../components/InputOTPGroup/InputOTPGroup';
import { InputOTPSlot } from '../components/InputOTPSlot/InputOTPSlot';
import { ResendCodeTimer } from '../components/ResendCodeTimer/ResendCodeTimer';
import { SendCodeResponseType } from '../types';

import { styles } from './UiAuthInputCode.styles';

type FormStatusType = {
  status: VariantProps<typeof styles>['status'];
  message?: string;
};

export interface UiAuthInputCodeProps {
  /**
   * Номер телефона, на который будет отправлен код подтверждения.
   * Должен быть в формате строки.
   */
  phoneNumber: string;
  /** Функция для отправки кода подтверждения. */
  onSendCode: (code: string) => Promise<SendCodeResponseType>;
  /** Функция для повторной отправки кода. */
  onResend: () => Promise<void> | void;
  /** Максимальная длина кода. Определяет количество символов, которые можно ввести. (по умолчанию 6 символов) */
  maxLength?: number;
  /**
   * Регулярное выражение для валидации ввода кода.
   * По умолчанию `REGEXP_ONLY_DIGITS_AND_CHARS` из `input-otp`.
   */
  pattern?: string;

  onComplete?: () => void;
}

export function UiAuthInputCode({
  phoneNumber,
  onSendCode,
  onResend,
  maxLength = 6,
  pattern = REGEXP_ONLY_DIGITS_AND_CHARS
}: UiAuthInputCodeProps) {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [formStatus, setFormStatus] = useState<FormStatusType>({
    status: 'default'
  });

  const slots = styles({ status: formStatus.status });

  const confirmNumber = async () => {
    if (value.length !== maxLength || formStatus.status === 'loading') return;
    inputRef.current?.blur();
    setFormStatus({ status: 'loading' });

    const {
      status: responseStatus,
      message,
      toastMessage
    } = await onSendCode(value);

    if (toastMessage) {
      toastBase(toastMessage, { status: responseStatus });
    }

    if (responseStatus === 'error') {
      setFormStatus({ status: 'error', message });
      return;
    }

    setFormStatus({ status: 'completed', message });
  };

  useEffect(() => {
    if (value.length !== maxLength) return;

    confirmNumber();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={slots.base()}>
      <span className={slots.headerText()}>
        {t('authInput.confirmNumber', {
          phoneNumber: utilFormatPhoneNumber(phoneNumber)
        })}
      </span>
      <InputOTP
        maxLength={maxLength}
        value={value}
        onChange={(value) => setValue(value)}
        pattern={pattern}
        ref={inputRef}
        onKeyDown={async (e) => {
          if (e.key === 'Enter') {
            await confirmNumber();
          }
        }}
      >
        <InputOTPGroup>
          {Array.from({ length: maxLength }).map((_, i) => (
            <InputOTPSlot
              key={i}
              index={i}
              className={slots.inputText()}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <span className={slots.helperText()}>{formStatus.message}</span>
      <ResendCodeTimer
        className={slots.resendText()}
        onResend={onResend}
      />
      {formStatus.status === 'loading' && (
        <div className={slots.loadingContainer()}>
          <span className={slots.loading()} />
        </div>
      )}
    </div>
  );
}
